/**
 * The single shared i18next instance — one translation source for React
 * (bound via react-i18next) and, in later phases, Phaser objects importing
 * `t` directly. Keeping both consumers on this one instance is what prevents
 * the vocabularies from ever drifting apart.
 *
 * Constraints this module upholds:
 * - No React components imported here (initReactI18next is a plugin, not UI).
 * - Resources are bundled and init is synchronous (initImmediate: false), so
 *   the language is resolved before the first React render — no flash and no
 *   Suspense requirement.
 * - escapeValue: false — React owns HTML escaping; i18next escaping on top
 *   would leak entities into visible text (audit §9). Never render translated
 *   strings via dangerouslySetInnerHTML.
 * - Module-singleton init + listener registration, so re-imports (tests)
 *   cannot create duplicate instances or duplicate global listeners.
 */
import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import {
  DEFAULT_LOCALE,
  SUPPORTED_LOCALES,
  applyDocumentLang,
  detectLocale,
  isAppLocale,
  persistLocale,
  type AppLocale,
} from './locale';
import enCommon from './locales/en/common.json';
import enGlossary from './locales/en/glossary.json';
import enNavigation from './locales/en/navigation.json';
import enAuth from './locales/en/auth.json';
import enSettings from './locales/en/settings.json';
import enHome from './locales/en/home.json';
import enPlay from './locales/en/play.json';
import enFriends from './locales/en/friends.json';
import enMessages from './locales/en/messages.json';
import enStats from './locales/en/stats.json';
import enLeaderboard from './locales/en/leaderboard.json';
import enAchievements from './locales/en/achievements.json';
import enCosmetics from './locales/en/cosmetics.json';
import enRules from './locales/en/rules.json';
import enTutorial from './locales/en/tutorial.json';
import enGameplay from './locales/en/gameplay.json';
import enDeleteAccount from './locales/en/deleteAccount.json';
import es419Common from './locales/es-419/common.json';
import es419Glossary from './locales/es-419/glossary.json';
import es419Navigation from './locales/es-419/navigation.json';
import es419Auth from './locales/es-419/auth.json';
import es419Settings from './locales/es-419/settings.json';
import es419Home from './locales/es-419/home.json';
import es419Play from './locales/es-419/play.json';
import es419Friends from './locales/es-419/friends.json';
import es419Messages from './locales/es-419/messages.json';
import es419Stats from './locales/es-419/stats.json';
import es419Leaderboard from './locales/es-419/leaderboard.json';
import es419Achievements from './locales/es-419/achievements.json';
import es419Cosmetics from './locales/es-419/cosmetics.json';
import es419Rules from './locales/es-419/rules.json';
import es419Tutorial from './locales/es-419/tutorial.json';
import es419Gameplay from './locales/es-419/gameplay.json';
import es419DeleteAccount from './locales/es-419/deleteAccount.json';

export const resources = {
  en: {
    common: enCommon,
    glossary: enGlossary,
    navigation: enNavigation,
    auth: enAuth,
    settings: enSettings,
    home: enHome,
    play: enPlay,
    friends: enFriends,
    messages: enMessages,
    stats: enStats,
    leaderboard: enLeaderboard,
    achievements: enAchievements,
    cosmetics: enCosmetics,
    rules: enRules,
    tutorial: enTutorial,
    gameplay: enGameplay,
    deleteAccount: enDeleteAccount,
  },
  'es-419': {
    common: es419Common,
    glossary: es419Glossary,
    navigation: es419Navigation,
    auth: es419Auth,
    settings: es419Settings,
    home: es419Home,
    play: es419Play,
    friends: es419Friends,
    messages: es419Messages,
    stats: es419Stats,
    leaderboard: es419Leaderboard,
    achievements: es419Achievements,
    cosmetics: es419Cosmetics,
    rules: es419Rules,
    tutorial: es419Tutorial,
    gameplay: es419Gameplay,
    deleteAccount: es419DeleteAccount,
  },
} as const;

export const i18n = i18next;

if (!i18n.isInitialized) {
  i18n.use(initReactI18next).init({
    resources,
    lng: detectLocale(),
    fallbackLng: DEFAULT_LOCALE,
    supportedLngs: [...SUPPORTED_LOCALES],
    defaultNS: 'common',
    ns: ['common', 'glossary', 'navigation', 'auth', 'settings', 'home', 'play', 'friends', 'messages', 'stats', 'leaderboard', 'achievements', 'cosmetics', 'rules', 'tutorial', 'gameplay', 'deleteAccount'],
    interpolation: { escapeValue: false },
    returnNull: false,
    // Bundled resources → synchronous init; the language is resolved before
    // the first React render. (i18next v24 renamed initImmediate → initAsync.)
    initAsync: false,
    react: { useSuspense: false },
    // Dev-only missing-key diagnostics; production falls back to English silently.
    saveMissing: import.meta.env.DEV === true,
    missingKeyHandler: (lngs: readonly string[], ns: string, key: string) => {
      console.warn(`[i18n] missing key "${ns}:${key}" for ${lngs.join(', ')}`);
    },
  });

  // Keep <html lang> synchronized on every runtime language change.
  i18n.on('languageChanged', (lng) => {
    if (isAppLocale(lng)) applyDocumentLang(lng);
  });
}

/** Translate outside React (Phaser, builders, non-component modules). */
export const t: typeof i18n.t = i18n.t.bind(i18n);

export function getCurrentLocale(): AppLocale {
  const lng = i18n.language;
  return isAppLocale(lng) ? lng : DEFAULT_LOCALE;
}

/**
 * Explicit user language selection: switches the live language (React
 * re-renders via react-i18next; <html lang> updates via the listener above)
 * and persists the choice. This is the ONLY path that writes storage.
 */
export async function setLanguage(locale: AppLocale): Promise<void> {
  if (!isAppLocale(locale)) return;
  await i18n.changeLanguage(locale);
  persistLocale(locale);
}

export type LanguageChangeListener = (locale: AppLocale) => void;

/**
 * Subscribe to language changes; returns the unsubscribe function. Callers
 * (e.g. a future GameScene handler) must invoke the returned function on
 * teardown — the pair prevents duplicate global listeners across scene
 * recreation and reconnects.
 */
export function onLanguageChanged(listener: LanguageChangeListener): () => void {
  const handler = (lng: string) => {
    if (isAppLocale(lng)) listener(lng);
  };
  i18n.on('languageChanged', handler);
  return () => i18n.off('languageChanged', handler);
}
